import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  FigureElement,
  FigureHandle,
  MarksHandle,
  ReadoutHandle,
  ReadoutRow,
  MarkSpec,
  GridHandle,
} from '@learnforge/demo-render';
import type { GeometryContext } from '../types';
import type { GeometryKey } from '../locales/keys';
import type { Vec2, Triangle } from '@learnforge/demo-math/geometry';
import {
  lerp,
  sub,
  dot,
  dist,
  dilatePoint,
  interceptRatios,
  similarCorrespondence,
  regularPolygonVertices,
} from '@learnforge/demo-math/geometry';
import { SIMILARITY_PRESETS } from '../data/similarity-presets';
import type { SimilarityMode } from '../data/similarity-presets';

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

export interface DilationModel {
  preImageVertices: Vec2[];
  imageVertices: Vec2[]; // each = dilatePoint(preImage[i], centre, k)
  centre: Vec2;
  k: number; // CLAMPED to range
  highlightedRatio: number; // = k (the ratio |A'B'| / |AB|)
}

/**
 * Build the dilation display model.
 * k clamp: [0.25, 3] foundation; [-3, 3] advanced (excluding 0 in advanced).
 */
export function buildDilationModel(
  preImageVertices: Vec2[],
  centre: Vec2,
  rawK: number,
  advanced: boolean,
): DilationModel {
  let k: number;
  if (advanced) {
    k = Math.max(-3, Math.min(3, rawK));
    // Avoid k = 0 (degenerate)
    if (k === 0) k = 0.01;
  } else {
    k = Math.max(0.25, Math.min(3, rawK));
  }

  const imageVertices = preImageVertices.map((v) => dilatePoint(v, centre, k));

  return {
    preImageVertices,
    imageVertices,
    centre,
    k,
    highlightedRatio: k,
  };
}

export interface InterceptModel {
  t: number; // clamped to (0.01, 0.99)
  ratioAD_DB: number; // = t/(1-t)
  ratioAE_EC: number; // = t/(1-t)
  isMidsegment: boolean; // true when |t - 0.5| < 1e-9
}

/**
 * Build the intercept (Thales) display model.
 */
export function buildInterceptModel(t: number): InterceptModel {
  const clamped = Math.max(0.01, Math.min(0.99, t));
  // tri parameter is unused in interceptRatios, pass a dummy
  const dummyTri: Triangle = {
    a: { x: 0, y: 0 },
    b: { x: 1, y: 0 },
    c: { x: 0.5, y: 1 },
  };
  const [ratioAD_DB, ratioAE_EC] = interceptRatios(dummyTri, clamped);
  const isMidsegment = Math.abs(clamped - 0.5) < 1e-9;

  return {
    t: clamped,
    ratioAD_DB,
    ratioAE_EC,
    isMidsegment,
  };
}

export interface SimilarTrianglesModel {
  correspondence: { mapping: [number, number, number]; k: number } | null;
  isSimilar: boolean; // correspondence !== null
}

/**
 * Build the similar-triangles display model.
 */
export function buildSimilarTrianglesModel(
  triA: Triangle,
  triB: Triangle,
  tol: number,
): SimilarTrianglesModel {
  const correspondence = similarCorrespondence(triA, triB, tol);
  return {
    correspondence,
    isSimilar: correspondence !== null,
  };
}

export interface AreaGridCount {
  preImageCount: number; // unit squares in pre-image bounding box: side^2
  imageCount: number; // unit squares in image bounding box: (k*side)^2
  areaRatio: number; // imageCount / preImageCount = k^2
}

/**
 * Count area grid squares for a square polygon of given side length and scale factor k.
 */
export function countAreaGrid(polygonSideLength: number, k: number): AreaGridCount {
  const preImageCount = Math.round(polygonSideLength) * Math.round(polygonSideLength);
  const imageSide = Math.round(k * polygonSideLength);
  const imageCount = imageSide * imageSide;
  const areaRatio = preImageCount > 0 ? imageCount / preImageCount : 0;

  return {
    preImageCount,
    imageCount,
    areaRatio,
  };
}

// ============================================================================
// Internal Geometry Helpers
// ============================================================================

const STAGE_W = 800;
const STAGE_H = 460;

/** Grid unit size in screen pixels */
const GRID_UNIT = 40;

/** Clamp k to its range for the given mode */
function buildKRange(advanced: boolean): { min: number; max: number } {
  if (advanced) {
    return { min: -3, max: 3 };
  }
  return { min: 0.25, max: 3 };
}

// Module-level constants for intercept base triangle
const INTERCEPT_A: Vec2 = { x: 400, y: 80 };
const INTERCEPT_B: Vec2 = { x: 180, y: 380 };
const INTERCEPT_C: Vec2 = { x: 620, y: 380 };

// Module-level default polygon centre
const DEFAULT_POLY_CENTRE: Vec2 = { x: 250, y: 230 };

// ============================================================================
// State shape
// ============================================================================

interface SimilarityState {
  mode: SimilarityMode;
  polyVerts: Vec2[];
  dilateCentre: Vec2;
  kValue: number;
  showRays: boolean;
  showAreaGrid: boolean;
  interceptT: number;
  triAVerts: [Vec2, Vec2, Vec2];
  triBVerts: [Vec2, Vec2, Vec2];
}

// ============================================================================
// Spec factory
// ============================================================================

const ADVANCED_MODES: readonly SimilarityMode[] = ['similar-triangles'];

function effectiveModeFor(state: SimilarityState, advanced: boolean): SimilarityMode {
  return !advanced && ADVANCED_MODES.includes(state.mode) ? 'dilation' : state.mode;
}

function defaultState(): SimilarityState {
  return {
    mode: 'dilation',
    polyVerts: regularPolygonVertices(4, 80, DEFAULT_POLY_CENTRE),
    dilateCentre: { x: 530, y: 230 },
    kValue: 1.5,
    showRays: true,
    showAreaGrid: false,
    interceptT: 0.5,
    triAVerts: [
      { x: 200, y: 350 },
      { x: 200, y: 150 },
      { x: 320, y: 350 },
    ],
    triBVerts: [
      { x: 480, y: 380 },
      { x: 480, y: 140 },
      { x: 660, y: 380 },
    ],
  };
}

function applyPresetLocal(
  state: SimilarityState,
  presetId: string,
  advanced: boolean,
): SimilarityState {
  const preset = SIMILARITY_PRESETS.find((p) => p.id === presetId);
  if (!preset || (preset.advancedOnly && !advanced)) return state;

  const next: SimilarityState = { ...state, mode: preset.mode };

  if (preset.k !== undefined) next.kValue = preset.k;
  if (preset.t !== undefined) next.interceptT = preset.t;
  if (preset.triAVertices) {
    next.triAVerts = preset.triAVertices.map(([x, y]) => ({ x, y })) as [Vec2, Vec2, Vec2];
  }
  if (preset.triBVertices) {
    next.triBVerts = preset.triBVertices.map(([x, y]) => ({ x, y })) as [Vec2, Vec2, Vec2];
  }

  return next;
}

function buildSpec(): ExperimentSpec<SimilarityState, GeometryKey, GeometryContext> {
  let figure: FigureHandle;
  let marks: MarksHandle;
  let readout: ReadoutHandle;
  let grid: GridHandle;
  let controllerRef: HarnessController<SimilarityState>;

  return {
    labelKey: 'experiment.similarity',
    demoId: '2d-geometry',
    advancedKey: 'similarity',

    initialState() {
      return defaultState();
    },

    applyPreset(state, presetId, ctx) {
      return applyPresetLocal(state, presetId, ctx.advanced);
    },

    setup(_ctx, controller) {
      controllerRef = controller;
      grid = controller.createGrid({
        width: STAGE_W,
        height: STAGE_H,
        unit: GRID_UNIT,
        faint: true,
      });
      figure = controller.createFigure();
      marks = controller.createMarks();
      readout = controller.createReadout();
    },

    render(state: SimilarityState, ctx): void {
      const eMode = effectiveModeFor(state, ctx.advanced);

      switch (eMode) {
        case 'dilation':
          renderDilationMode(state, ctx.advanced);
          break;
        case 'intercept':
          renderInterceptMode(state);
          break;
        case 'similar-triangles':
          renderSimilarTrianglesMode(state);
          break;
      }

      function renderDilationMode(state: SimilarityState, advanced: boolean): void {
        const model = buildDilationModel(
          state.polyVerts,
          state.dilateCentre,
          state.kValue,
          advanced,
        );
        const elems: FigureElement[] = [];

        // Pre-image polygon points
        for (let i = 0; i < model.preImageVertices.length; i++) {
          elems.push({
            kind: 'point',
            id: `poly-pt-${i}`,
            at: model.preImageVertices[i]!,
            draggable: {},
            style: { className: 'geo-preimage-pt' },
          });
        }

        // Pre-image polygon
        const preImagePtIds = model.preImageVertices.map((_, i) => `poly-pt-${i}`);
        elems.push({
          kind: 'polygon',
          id: 'poly-preimage',
          vertices: preImagePtIds,
          style: { className: 'geo-preimage-poly' },
        });

        // Image polygon points
        for (let i = 0; i < model.imageVertices.length; i++) {
          elems.push({
            kind: 'point',
            id: `poly-img-${i}`,
            at: model.imageVertices[i]!,
            style: { className: 'geo-image-pt' },
          });
        }

        // Image polygon
        const imagePtIds = model.imageVertices.map((_, i) => `poly-img-${i}`);
        elems.push({
          kind: 'polygon',
          id: 'poly-image',
          vertices: imagePtIds,
          style: { className: 'geo-image-poly' },
        });

        // Dilation centre (draggable)
        elems.push({
          kind: 'point',
          id: 'dil-centre',
          at: model.centre,
          draggable: {},
          style: { className: 'geo-dilation-centre' },
        });

        // Rays from centre through pre-image to image vertices
        if (state.showRays) {
          for (let i = 0; i < model.preImageVertices.length; i++) {
            const preId = `poly-pt-${i}`;
            const imgId = `poly-img-${i}`;
            elems.push({
              kind: 'ray',
              id: `ray-${i}`,
              through: ['dil-centre', preId],
              style: { className: 'geo-ray', dashed: true },
            });
            // Segment from centre to image point for clarity
            elems.push({
              kind: 'segment',
              id: `ray-seg-${i}`,
              from: 'dil-centre',
              to: imgId,
              style: { className: 'geo-ray-seg', dashed: true },
            });
          }
        }

        // Labels for pre-image and image first vertex
        if (model.preImageVertices.length > 0) {
          elems.push({
            kind: 'label',
            id: 'lbl-preimage',
            anchor: 'poly-pt-0',
            offset: { x: -18, y: -10 },
            text: 'A',
          });
          elems.push({
            kind: 'label',
            id: 'lbl-image',
            anchor: 'poly-img-0',
            offset: { x: -18, y: -10 },
            text: "A'",
          });
        }

        figure.update(elems);
        marks.update([]);

        // Area grid — fill both pre-image and image bounding boxes
        if (state.showAreaGrid && advanced) {
          // Approximate the edge-length of the pre-image polygon (first edge in screen px)
          const screenSide = Math.abs(
            model.preImageVertices.length >= 2
              ? Math.sqrt(
                  Math.pow(
                    (model.preImageVertices[1]?.x ?? 0) - (model.preImageVertices[0]?.x ?? 0),
                    2,
                  ) +
                    Math.pow(
                      (model.preImageVertices[1]?.y ?? 0) - (model.preImageVertices[0]?.y ?? 0),
                      2,
                    ),
                )
              : 80,
          );
          const worldSide = Math.max(1, Math.round(screenSide / GRID_UNIT));
          const absK = Math.abs(model.k);
          const imageWorldSide = Math.max(1, Math.round(absK * worldSide));

          // Pre-image bounding box in world units (centred at pre-image polygon centroid)
          const polyCx =
            model.preImageVertices.reduce((s, v) => s + v.x, 0) / model.preImageVertices.length;
          const polyCy =
            model.preImageVertices.reduce((s, v) => s + v.y, 0) / model.preImageVertices.length;
          const preWx = polyCx / GRID_UNIT;
          const preWy = polyCy / GRID_UNIT;
          const preHalf = worldSide / 2;

          // Image bounding box in world units (centred at image polygon centroid)
          const imgCx =
            model.imageVertices.reduce((s, v) => s + v.x, 0) / model.imageVertices.length;
          const imgCy =
            model.imageVertices.reduce((s, v) => s + v.y, 0) / model.imageVertices.length;
          const imgWx = imgCx / GRID_UNIT;
          const imgWy = imgCy / GRID_UNIT;
          const imgHalf = imageWorldSide / 2;

          // Union bounding box so a single fillRegion call covers both polygons
          const unionX0 = Math.min(preWx - preHalf, imgWx - imgHalf);
          const unionY0 = Math.min(preWy - preHalf, imgWy - imgHalf);
          const unionX1 = Math.max(preWx + preHalf, imgWx + imgHalf);
          const unionY1 = Math.max(preWy + preHalf, imgWy + imgHalf);

          grid.fillRegion(unionX0, unionY0, unionX1, unionY1);
        } else {
          grid.clearFill();
        }

        const rows: ReadoutRow[] = [
          {
            id: 'dil-ratio',
            lhs: "|A'B'| / |AB|",
            relation: '=',
            rhs: model.highlightedRatio.toFixed(2),
            emphasis: 'invariant',
          },
        ];

        if (state.showAreaGrid && advanced) {
          const screenSide =
            model.preImageVertices.length >= 2
              ? Math.sqrt(
                  Math.pow(
                    (model.preImageVertices[1]?.x ?? 0) - (model.preImageVertices[0]?.x ?? 0),
                    2,
                  ) +
                    Math.pow(
                      (model.preImageVertices[1]?.y ?? 0) - (model.preImageVertices[0]?.y ?? 0),
                      2,
                    ),
                )
              : 80;
          const worldSide = Math.max(1, Math.round(screenSide / GRID_UNIT));
          const areaInfo = countAreaGrid(worldSide, Math.abs(model.k));
          rows.push({
            id: 'area-ratio',
            lhs: 'area(image) / area(pre-image)',
            relation: '=',
            rhs: `${areaInfo.areaRatio.toFixed(2)} (k²)`,
            emphasis: 'invariant',
          });
        }

        readout.update(rows);
      }

      function renderInterceptMode(state: SimilarityState): void {
        const model = buildInterceptModel(state.interceptT);
        const A = INTERCEPT_A;
        const B = INTERCEPT_B;
        const C = INTERCEPT_C;

        // D = lerp(A, B, t), E = lerp(A, C, t)
        const D = lerp(A, B, model.t);
        const E = lerp(A, C, model.t);

        // Drag spec for cut-D: constrained to segment AB, snaps to midpoint
        const ab = sub(B, A);
        const abLen2 = dot(ab, ab);
        const midAB = lerp(A, B, 0.5);
        const SNAP_TOLERANCE_PX = 15;
        const cutDDragSpec = {
          constraint: (p: Vec2): Vec2 => {
            if (abLen2 < 1e-10) return A;
            const ap = sub(p, A);
            const rawT = dot(ap, ab) / abLen2;
            const clampedT = Math.max(0.05, Math.min(0.95, rawT));
            return lerp(A, B, clampedT);
          },
          snap: (p: Vec2): Vec2 | null => {
            // p is already constrained onto AB; snap to midpoint if close
            return dist(p, midAB) < SNAP_TOLERANCE_PX ? midAB : null;
          },
        };

        const elems: FigureElement[] = [
          { kind: 'point', id: 'tri-A', at: A },
          { kind: 'point', id: 'tri-B', at: B },
          { kind: 'point', id: 'tri-C', at: C },
          {
            kind: 'point',
            id: 'cut-D',
            at: D,
            draggable: cutDDragSpec,
            style: { className: 'geo-intercept-pt' },
          },
          { kind: 'point', id: 'tri-E', at: E, style: { className: 'geo-intercept-pt' } },
          // Triangle sides
          {
            kind: 'segment',
            id: 'seg-AB',
            from: 'tri-A',
            to: 'tri-B',
            style: { className: 'geo-tri-side' },
          },
          {
            kind: 'segment',
            id: 'seg-AC',
            from: 'tri-A',
            to: 'tri-C',
            style: { className: 'geo-tri-side' },
          },
          {
            kind: 'segment',
            id: 'seg-BC',
            from: 'tri-B',
            to: 'tri-C',
            style: { className: 'geo-tri-base' },
          },
          // Midsegment DE (parallel to BC)
          {
            kind: 'segment',
            id: 'seg-DE',
            from: 'cut-D',
            to: 'tri-E',
            style: { className: 'geo-intercept-seg' },
          },
          // Labels
          { kind: 'label', id: 'lbl-A', anchor: 'tri-A', offset: { x: 0, y: -14 }, text: 'A' },
          { kind: 'label', id: 'lbl-B', anchor: 'tri-B', offset: { x: -14, y: 12 }, text: 'B' },
          { kind: 'label', id: 'lbl-C', anchor: 'tri-C', offset: { x: 10, y: 12 }, text: 'C' },
          { kind: 'label', id: 'lbl-D', anchor: 'cut-D', offset: { x: -16, y: 0 }, text: 'D' },
          { kind: 'label', id: 'lbl-E', anchor: 'tri-E', offset: { x: 10, y: 0 }, text: 'E' },
        ];

        figure.update(elems);

        const markSpecs: MarkSpec[] = [
          { kind: 'parallel', id: 'par-BC', a: B, b: C, arrows: 1 },
          { kind: 'parallel', id: 'par-DE', a: D, b: E, arrows: 1 },
        ];
        marks.update(markSpecs);

        const rows: ReadoutRow[] = [
          {
            id: 'intercept-ratio',
            lhs: 'AD/DB = AE/EC',
            relation: '=',
            rhs: model.ratioAD_DB.toFixed(2),
            emphasis: 'invariant',
          },
        ];

        if (model.isMidsegment) {
          rows.push({
            id: 'midsegment-badge',
            lhs: 'k = ½',
            relation: '=',
            rhs: 'midsegment',
            emphasis: 'invariant',
          });
        }

        readout.update(rows);
        grid.clearFill();
      }

      function renderSimilarTrianglesMode(state: SimilarityState): void {
        const triA: Triangle = {
          a: state.triAVerts[0],
          b: state.triAVerts[1],
          c: state.triAVerts[2],
        };
        const triB: Triangle = {
          a: state.triBVerts[0],
          b: state.triBVerts[1],
          c: state.triBVerts[2],
        };

        const model = buildSimilarTrianglesModel(triA, triB, 5);

        const elems: FigureElement[] = [];

        // Triangle A vertices (draggable)
        for (let i = 0; i < 3; i++) {
          elems.push({
            kind: 'point',
            id: `triA-${i}`,
            at: state.triAVerts[i]!,
            draggable: {},
            style: { className: 'geo-preimage-pt' },
          });
        }
        elems.push({
          kind: 'polygon',
          id: 'poly-triA',
          vertices: ['triA-0', 'triA-1', 'triA-2'],
          style: { className: 'geo-preimage-poly' },
        });
        elems.push(
          {
            kind: 'label',
            id: 'lbl-triA-0',
            anchor: 'triA-0',
            offset: { x: -14, y: 10 },
            text: 'A',
          },
          {
            kind: 'label',
            id: 'lbl-triA-1',
            anchor: 'triA-1',
            offset: { x: -14, y: -8 },
            text: 'B',
          },
          { kind: 'label', id: 'lbl-triA-2', anchor: 'triA-2', offset: { x: 8, y: 10 }, text: 'C' },
        );

        // Triangle B vertices (draggable)
        for (let i = 0; i < 3; i++) {
          elems.push({
            kind: 'point',
            id: `triB-${i}`,
            at: state.triBVerts[i]!,
            draggable: {},
            style: { className: 'geo-image-pt' },
          });
        }
        elems.push({
          kind: 'polygon',
          id: 'poly-triB',
          vertices: ['triB-0', 'triB-1', 'triB-2'],
          style: { className: 'geo-image-poly' },
        });
        elems.push(
          {
            kind: 'label',
            id: 'lbl-triB-0',
            anchor: 'triB-0',
            offset: { x: 8, y: 10 },
            text: "A'",
          },
          {
            kind: 'label',
            id: 'lbl-triB-1',
            anchor: 'triB-1',
            offset: { x: 8, y: -8 },
            text: "B'",
          },
          {
            kind: 'label',
            id: 'lbl-triB-2',
            anchor: 'triB-2',
            offset: { x: 8, y: 10 },
            text: "C'",
          },
        );

        figure.update(elems);

        // Angle marks for matched vertices when similar
        const markSpecs: MarkSpec[] = [];
        if (model.isSimilar && model.correspondence) {
          const mapping = model.correspondence.mapping;
          const vertsA = [state.triAVerts[0], state.triAVerts[1], state.triAVerts[2]];
          const vertsB = [state.triBVerts[0], state.triBVerts[1], state.triBVerts[2]];

          for (let i = 0; i < 3; i++) {
            const mappedIdx = mapping[i]!;
            const strokes = (i + 1) as 1 | 2 | 3;

            // Angle arc for triA vertex i
            const prevA = vertsA[(i + 2) % 3]!;
            const nextA = vertsA[(i + 1) % 3]!;
            markSpecs.push({
              kind: 'angle',
              id: `angle-A-${i}`,
              vertex: vertsA[i]!,
              from: prevA,
              to: nextA,
              strokes,
            });

            // Angle arc for triB corresponding vertex
            const prevB = vertsB[(mappedIdx + 2) % 3]!;
            const nextB = vertsB[(mappedIdx + 1) % 3]!;
            markSpecs.push({
              kind: 'angle',
              id: `angle-B-${i}`,
              vertex: vertsB[mappedIdx]!,
              from: prevB,
              to: nextB,
              strokes,
            });
          }
        }
        marks.update(markSpecs);

        const rows: ReadoutRow[] = [];
        if (model.isSimilar && model.correspondence) {
          rows.push({
            id: 'similar-badge',
            lhs: '△ABC ~ △A′B′C′',
            relation: '=',
            rhs: `k = ${model.correspondence.k.toFixed(2)}`,
            emphasis: 'invariant',
          });
        } else {
          rows.push({
            id: 'not-similar-badge',
            lhs: '△ABC',
            relation: '≈',
            rhs: '△A′B′C′ ?',
            emphasis: 'broken',
          });
        }
        readout.update(rows);
        grid.clearFill();
      }
    },

    panel(state: SimilarityState, ctx): PanelRow<GeometryKey, SimilarityState>[] {
      const eMode = effectiveModeFor(state, ctx.advanced);

      const rows: PanelRow<GeometryKey, SimilarityState>[] = [];

      // Mode dropdown
      const modeOptions: { value: string; labelKey: GeometryKey }[] = [
        { value: 'dilation', labelKey: 'similarity.mode.dilation' },
        { value: 'intercept', labelKey: 'similarity.mode.intercept' },
      ];
      if (ctx.advanced) {
        modeOptions.push({
          value: 'similar-triangles',
          labelKey: 'similarity.mode.similar-triangles',
        });
      }

      rows.push({
        kind: 'dropdown',
        id: 'similarity-mode',
        labelKey: 'panel.mode',
        options: modeOptions,
        value: eMode,
        onChange: (v) => ({ ...state, mode: v as SimilarityMode }),
      });

      // k slider — ONLY when eMode === 'dilation'
      if (eMode === 'dilation') {
        const range = buildKRange(ctx.advanced);
        const displayK = Math.max(range.min, Math.min(range.max, state.kValue));
        rows.push({
          kind: 'slider',
          id: 'similarity-k',
          labelKey: 'similarity.panel.k',
          min: range.min,
          max: range.max,
          step: 0.1,
          value: displayK,
          format: (v) => v.toFixed(2),
          onChange: (v) => ({ ...state, kValue: v }),
        });
      }

      // show-rays toggle — ONLY when eMode === 'dilation'
      if (eMode === 'dilation') {
        rows.push({
          kind: 'toggle',
          labelKey: 'similarity.panel.show-rays',
          value: state.showRays,
          onChange: (v) => ({ ...state, showRays: v }),
        });
      }

      // show-area-grid toggle — ONLY when ctx.advanced && eMode === 'dilation'
      if (ctx.advanced && eMode === 'dilation') {
        rows.push({
          kind: 'toggle',
          labelKey: 'similarity.panel.show-area-grid',
          value: state.showAreaGrid,
          onChange: (v) => ({ ...state, showAreaGrid: v }),
        });
      }

      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions: (state: SimilarityState, ctx): ActionBarSpec<GeometryKey, SimilarityState> => {
      const eMode = effectiveModeFor(state, ctx.advanced);
      return {
        onReset: () => defaultState(),
        onRandomise: (s) => {
          if (eMode === 'dilation') {
            const range = buildKRange(ctx.advanced);
            const span = range.max - range.min;
            let k = Math.round((range.min + ctx.prng() * span) * 10) / 10;
            k = Math.max(range.min, Math.min(range.max, k));
            return { ...s, kValue: k };
          } else if (eMode === 'intercept') {
            return { ...s, interceptT: Math.round((0.1 + ctx.prng() * 0.8) * 100) / 100 };
          }
          return s;
        },
        presets: SIMILARITY_PRESETS.filter(
          (p) => p.mode === eMode && (ctx.advanced || !p.advancedOnly),
        ).map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id, ctx.advanced),
      };
    },

    onDrag(state: SimilarityState, pointId: string, at: Vec2): SimilarityState {
      const eMode = effectiveModeFor(state, controllerRef.advanced);

      if (eMode === 'dilation') {
        if (pointId === 'dil-centre') {
          return { ...state, dilateCentre: at };
        }
        if (pointId.startsWith('poly-pt-')) {
          const i = parseInt(pointId.slice(8), 10);
          if (i >= 0 && i < state.polyVerts.length) {
            const polyVerts = state.polyVerts.slice();
            polyVerts[i] = at;
            return { ...state, polyVerts };
          }
        }
      } else if (eMode === 'similar-triangles') {
        if (pointId.startsWith('triA-')) {
          const i = parseInt(pointId.slice(5), 10);
          if (i >= 0 && i < 3) {
            const triAVerts = [...state.triAVerts] as [Vec2, Vec2, Vec2];
            triAVerts[i] = at;
            return { ...state, triAVerts };
          }
        }
        if (pointId.startsWith('triB-')) {
          const i = parseInt(pointId.slice(5), 10);
          if (i >= 0 && i < 3) {
            const triBVerts = [...state.triBVerts] as [Vec2, Vec2, Vec2];
            triBVerts[i] = at;
            return { ...state, triBVerts };
          }
        }
      } else if (eMode === 'intercept') {
        if (pointId === 'cut-D') {
          const ab = sub(INTERCEPT_B, INTERCEPT_A);
          const abLen2 = dot(ab, ab);
          if (abLen2 > 1e-10) {
            const ad = sub(at, INTERCEPT_A);
            const raw = dot(ad, ab) / abLen2;
            const interceptT = Math.max(0.05, Math.min(0.95, raw));
            return { ...state, interceptT };
          }
        }
      }

      return state;
    },

    applyShotState(state: SimilarityState, shotId: string): SimilarityState {
      if (shotId === 'similarity') {
        return applyPresetLocal(state, 'midsegment-half', true);
      }
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
